import { DateTime } from 'luxon';
import { Clickable, ClickHandler } from 'dg-web-shared/ui/Clickable.tsx';
import * as Icons16 from 'dg-web-shared/ui/icons/Icons16.tsx';
import { Icon16 } from '../icons/Icon.tsx';
import { range } from 'dg-web-shared/lib/ArrayUtil.ts';
import { DateSelectHandler } from './DatePicker.tsx';
import {
    getOrElse,
    isDefined,
    isUndefined,
    Maybe,
} from 'dg-web-shared/lib/MaybeV2.ts';
import { css } from '@emotion/css';
import { ColorHex } from '../Colors.ts';
import { OperatorTypo } from '../OperatorTypo.ts';
import React, { type JSX } from 'react';
import { Typo } from 'dg-web-shared/ui/typo.ts';

interface YearNavigationProps {
    visibleDate: DateTime;
    onPrev: Maybe<ClickHandler>;
    onNext: Maybe<ClickHandler>;
    onCurrent: Maybe<ClickHandler>;
}

export function iconButtonStyleParkingPortal(
    disabled: boolean,
    type: 'prev' | 'next',
): string {
    return css([
        {
            position: 'absolute',
            width: '40px',
            height: '100%',
            padding: '12px',
            ...OperatorTypo.bodyC,
        },
        disabled && {
            color: ColorHex.rgba(ColorHex.darkblue, 0.4),
        },
        !disabled && {
            '&:hover': {
                background: ColorHex.lightblue,
                color: ColorHex.white,
            },
        },
        type === 'prev' && {
            left: 0,
        },
        type === 'next' && {
            right: 0,
        },
    ]);
}

const YearNavigation = (p: YearNavigationProps): JSX.Element => (
    <div
        className={css({
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '40px',
            background: ColorHex.rgba(ColorHex.lightblue, 0.2),
            color: ColorHex.darkblue,
        })}
    >
        <Clickable
            element="div"
            className={iconButtonStyleParkingPortal(
                isUndefined(p.onPrev),
                'prev',
            )}
            onClick={p.onPrev}
            disabled={isUndefined(p.onPrev)}
        >
            <Icon16 icon={Icons16.chevronLeft} />
        </Clickable>
        <Clickable
            element="div"
            className={iconButtonStyleParkingPortal(
                isUndefined(p.onNext),
                'next',
            )}
            onClick={p.onNext}
            disabled={isUndefined(p.onNext)}
        >
            <Icon16 icon={Icons16.chevronRight} />
        </Clickable>
        <Clickable
            element="div"
            className={css([
                {
                    margin: '0 40px',
                    textAlign: 'center',
                    height: '100%',
                    paddingTop: '11px',
                    ...OperatorTypo.bodyC,
                },
                !isUndefined(p.onCurrent) && {
                    '&:hover': {
                        background: ColorHex.lightblue,
                        color: ColorHex.white,
                    },
                },
            ])}
            onClick={p.onCurrent}
            disabled={isUndefined(p.onCurrent)}
        >
            {p.visibleDate.toFormat('yyyy')}
        </Clickable>
    </div>
);

interface MonthsProps {
    visibleDate: DateTime;
    selectedDate: Maybe<DateTime>;
    onSelect: DateSelectHandler;
    minDate: Maybe<DateTime>;
    maxDate: Maybe<DateTime>;
    language: string;
}

const Months = (p: MonthsProps): JSX.Element => {
    const { minDate, maxDate, selectedDate } = p;
    return (
        <div
            className={css({
                position: 'absolute',
                top: '40px',
                bottom: 0,
                width: '100%',
                overflow: 'hidden',
                background: ColorHex.rgba(ColorHex.lightblue, 0.1),
            })}
        >
            {range(12).map(i => {
                const m = p.visibleDate.startOf('year').plus({ months: i });
                const active =
                    (isUndefined(minDate) || m.endOf('month') > minDate) &&
                    (isUndefined(maxDate) || m < maxDate);
                const dataSelected =
                    isDefined(selectedDate) && m.hasSame(selectedDate, 'month');
                return (
                    <Clickable
                        element="div"
                        className={css([
                            {
                                color: ColorHex.darkblue,
                                height: '40px',
                                width: '93px',
                                float: 'left',
                                textAlign: 'center',
                                marginTop: '15px',
                                marginBottom: '15px',
                                ...Typo.captionC1,
                                lineHeight: '40px',
                            },
                            dataSelected && {
                                color: ColorHex.white,
                                background: ColorHex.darkblue,
                            },
                            active && {
                                '&:hover': {
                                    color: ColorHex.white,
                                    background: ColorHex.lightblue,
                                },
                            },
                            !active && {
                                color: ColorHex.rgba(ColorHex.darkblue, 0.4),
                            },
                        ])}
                        key={m.toSeconds()}
                        onClick={() => active && p.onSelect(m)}
                        disabled={!active}
                        data-selected={
                            isDefined(selectedDate) &&
                            m.hasSame(selectedDate, 'month')
                        }
                    >
                        {m
                            .setLocale(p.language)
                            .toFormat('LLL')
                            .replace('.', '')}
                    </Clickable>
                );
            })}
        </div>
    );
};

interface MonthPickerProps {
    currentDate: DateTime;
    selectedDate: Maybe<DateTime>;
    onSelect: DateSelectHandler;
    onClose?: Maybe<ClickHandler>;
    minDate: Maybe<DateTime>;
    maxDate: Maybe<DateTime>;
    language: string;
}

export function MonthPicker(props: MonthPickerProps) {
    const dateFromProps = getOrElse(props.selectedDate, props.currentDate);

    // The class reassigned its state object directly in
    // UNSAFE_componentWillReceiveProps, so year paging was discarded whenever
    // new props arrived. This is the documented equivalent: keep the paged
    // value in state, and reset it when the date coming from props changes.
    const [visibleDate, setVisibleDate] = React.useState(dateFromProps);
    const [previousFromProps, setPreviousFromProps] =
        React.useState(dateFromProps);
    if (previousFromProps !== dateFromProps) {
        setPreviousFromProps(dateFromProps);
        setVisibleDate(dateFromProps);
    }

    function getPrevYearHandler(): Maybe<ClickHandler> {
        const { minDate } = props;
        if (
            isDefined(minDate) &&
            visibleDate &&
            visibleDate.startOf('year') < minDate
        ) {
            return null;
        } else {
            return () => {
                if (!visibleDate) {
                    return;
                }

                setVisibleDate(visibleDate.minus({ years: 1 }));
            };
        }
    }

    function getNextYearhHandler(): Maybe<ClickHandler> {
        const { maxDate } = props;
        if (
            isDefined(maxDate) &&
            visibleDate &&
            visibleDate.endOf('year') > maxDate
        ) {
            return null;
        } else {
            return () => {
                if (!visibleDate) {
                    return;
                }

                setVisibleDate(visibleDate.plus({ years: 1 }));
            };
        }
    }

    return (
        <div
            className={css({
                height: '320px',
                width: '280px',
                background: ColorHex.white,
                position: 'relative',
            })}
        >
            <YearNavigation
                onCurrent={props.onClose}
                onNext={getNextYearhHandler()}
                onPrev={getPrevYearHandler()}
                visibleDate={visibleDate || DateTime.now()}
            />
            <Months
                visibleDate={visibleDate || DateTime.now()}
                selectedDate={props.selectedDate}
                onSelect={props.onSelect}
                minDate={props.minDate}
                maxDate={props.maxDate}
                language={props.language}
            />
        </div>
    );
}
